import { useLogin } from "../authConfig";

const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

const getHeaders = async (idToken: string | undefined): Promise<Record<string, string>> => {
    if (useLogin && idToken) {
        return { Authorization: `Bearer ${idToken}` };
    }

    return {};
};

type ImportNotesStreamEvent =
    | { event: "started" }
    | { event: "extracted"; char_count: number; page_count: number }
    | { event: "split"; total: number }
    | { event: "entry_start"; index: number; raw_text: string; hint_date: string | null; hint_time: string | null }
    | { event: "entry_delta"; index: number; delta: string }
    | { event: "entry_complete"; index: number; formatted: string }
    | { event: "entry_error"; index: number; error: string }
    // `split_chunk_failed` is a fatal error: a chunk could not be split after one retry.
    // The caller must NOT save any partial drafts — care-documentation safety.
    | { event: "error"; error: "split_chunk_failed" | string; detail?: string }
    | { event: "done"; success: number; failed: number };

export class ImportNotesError extends Error {
    status: number;
    data: any;

    constructor(message: string, status: number, data: any) {
        super(message);
        this.name = "ImportNotesError";
        this.status = status;
        this.data = data;
    }
}

export async function importNotesFromPdfStream(
    projectId: string,
    file: File,
    idToken: string | undefined,
    onEvent: (event: ImportNotesStreamEvent) => void,
    signal?: AbortSignal
): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/import_pdf`, {
        method: "POST",
        headers: await getHeaders(idToken),
        body: formData,
        credentials: "same-origin",
        signal
    });

    if (!response.ok) {
        let errorData: any = null;
        try {
            errorData = await response.json();
        } catch {
            errorData = { error: `Import failed with status ${response.status}` };
        }
        throw new ImportNotesError(errorData?.error || "Import failed", response.status, errorData);
    }

    if (!response.body) {
        throw new Error("Import stream missing response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
            if (!line.trim()) {
                continue;
            }
            onEvent(JSON.parse(line) as ImportNotesStreamEvent);
        }

        if (done) {
            break;
        }
    }

    if (buffer.trim()) {
        onEvent(JSON.parse(buffer) as ImportNotesStreamEvent);
    }
}

export type { ImportNotesStreamEvent };
